import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  List,
  Box,
  People,
  BarChart,
  MenuUp as MenuIcon,
  X,
  BoxArrowRight as LogOut,
  ChevronRight,
  BoxSeam as Package,
} from "react-bootstrap-icons";
import "../styles/Home.css";

const navItems = [
  {
    href: "/products",
    label: "Products",
    icon: <Package size={18} />,
    description: "Manage your product catalog",
  },
  {
    href: "/brands",
    label: "Brands",
    icon: <Box size={18} />,
    description: "Organize product brands",
  },
  {
    href: "/categories",
    label: "Categories",
    icon: <List size={18} />,
    description: "Product categorization",
  },
  {
    href: "/orders",
    label: "Orders",
    icon: <Box size={18} />,
    description: "Track and manage orders",
  },
  {
    href: "/agents",
    label: "Agents",
    icon: <People size={18} />,
    description: "Manage delivery agents",
  },
  {
    href: "/reports",
    label: "Reports",
    icon: <BarChart size={18} />,
    description: "View analytics and reports",
  },
];

const Home = ({ children }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  let user = null;
  try {
    const raw = localStorage.getItem("user");
    if (raw) user = JSON.parse(raw);
  } catch {}

  const displayName = user?.name || user?.fullName || "Distributor";
  const displayEmail = user?.email || user?.username || "admin@dms.app";

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="main-layout">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="mobile-overlay d-lg-none" onClick={closeSidebar} />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center gap-3">
              <div className="brand-logo">
                <Package size={24} className="text-primary" />
              </div>
              <div className="brand-info">
                <h5 className="brand-title mb-0 d-lg-none">
                  {process.env.REACT_APP_CLIENT_NAME || "Store Admin"}
                </h5>
                <h6 className="brand-name mb-0 d-none d-lg-block">
                  {process.env.REACT_APP_CLIENT_NAME || "Store Admin"}
                </h6>
                <small className="text-muted d-lg-none">
                  Management Portal
                </small>
                <small className="text-muted d-none d-lg-block">
                  Admin Panel
                </small>
              </div>
            </div>
            <button
              className="btn btn-ghost d-lg-none p-2"
              onClick={closeSidebar}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Sidebar Navigation */}
        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">
              <span>Main Navigation</span>
            </div>
            <ul className="nav-list">
              {navItems.map((item) => (
                <li key={item.href}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      `nav-item ${isActive ? "nav-item-active" : ""}`
                    }
                    onClick={closeSidebar}
                  >
                    <div className="nav-item-content">
                      <div className="nav-item-icon">{item.icon}</div>
                      <div className="nav-item-text">
                        <span className="nav-item-label">{item.label}</span>
                        <small className="nav-item-description">
                          {item.description}
                        </small>
                      </div>
                    </div>
                    <ChevronRight size={14} className="nav-item-chevron" />
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          <div className="user-profile d-lg-none">
            <div className="user-avatar">
              {displayName.slice(0, 2).toUpperCase()}
            </div>
            <div className="user-info">
              <div className="user-name">{displayName}</div>
              <div className="user-email">{displayEmail}</div>
            </div>
          </div>
          <button
            className="logout-button d-lg-none"
            onClick={handleLogout}
            title="Sign out"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>

          {/* Desktop Sidebar Footer - Just branding */}
          <div className="sidebar-footer-desktop d-none d-lg-block">
            <div className="text-center text-gray-400 small">
              <div>Powered by VickeyApps</div>
              <div className="text-xs">v2.0.1</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="content-area">
        {/* Mobile Header */}
        <div className="mobile-header d-lg-none">
          <button
            className="btn btn-ghost"
            onClick={() => setSidebarOpen(true)}
          >
            <MenuIcon size={20} />
          </button>
          <h6 className="mb-0">
            {process.env.REACT_APP_CLIENT_NAME || "Store Admin"}
          </h6>
          <button
            className="btn btn-ghost mobile-logout"
            onClick={handleLogout}
            title="Sign out"
          >
            <LogOut size={18} />
          </button>
        </div>

        {/* Desktop Header */}
        <div className="desktop-header d-none d-lg-flex">
          <div className="desktop-header-left">
            <h5 className="mb-0 text-gray-900 fw-semibold">
              {process.env.REACT_APP_CLIENT_NAME || "Store Admin"}
            </h5>
            <div className="desktop-breadcrumb">
              <span className="text-gray-500 small">
                Welcome back, {displayName}
              </span>
              <span className="text-gray-300 mx-2">•</span>
              <span className="text-gray-400 small">
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>
          </div>
          <div className="desktop-header-center">
            <div className="status-indicator">
              <div className="status-dot status-online"></div>
              <span className="status-text">System Online</span>
            </div>
          </div>
          <div className="desktop-header-right">
            <div className="user-profile-desktop">
              <div className="user-avatar-small">
                {displayName.slice(0, 2).toUpperCase()}
              </div>
              <div className="user-info-desktop">
                <div className="user-name-small">{displayName}</div>
                <div className="user-role-small">Administrator</div>
              </div>
              <button
                className="logout-button-desktop"
                onClick={handleLogout}
                title="Sign out"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>

        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Home;
